// src/locales/en/offlineSales.js
// Stage 7: renamed namespace for the POS feature (offlineSales.* replaces pos.* in new pages).
// pos.js itself stays untouched/live for the old pages. `posLegacyKeys` below is the full
// pos.js key set copied verbatim (per-language) so offlineSales.* has complete parity with the
// original namespace; `newPageKeys` are the additional keys needed for the new
// CreateOfflineSalesOrderPage and ReceivablesPage (kept as their own block since these exact
// key names are the ones the actual page components call via t()).
const posLegacyKeys = {
  // Navigation / general
  posTerminal: 'POS Terminal',
  posOrders: 'POS Orders',
  posCustomers: 'POS Customers',
  posSessions: 'Cashier Sessions',
  posSettings: 'POS Settings',
  pointOfSale: 'Point of Sale',

  // Terminal top bar
  session: 'Session',
  sessionOpen: 'Open',
  sessionClosed: 'Closed',
  noSession: 'No Session',
  openSession: 'Open Session',
  closeSession: 'Close Session',
  exitTerminal: 'Exit Terminal',
  selectWarehouse: 'Select Warehouse',

  // Cart
  cart: 'Cart',
  emptyCart: 'Cart is empty',
  emptyCartHint: 'Search or scan a product to add it to the cart',
  qty: 'Qty',
  lineDiscount: 'Discount',
  lineTotal: 'Total',
  subtotal: 'Subtotal',
  itemDiscount: 'Item Discounts',
  loyaltyDiscount: 'Loyalty Discount',

  // Actions
  holdOrder: 'Hold',
  clearCart: 'Clear',
  applyDiscount: 'Discount',
  redeemLoyalty: 'Loyalty',
  charge: 'Charge',
  completeSale: 'Complete Sale',
  printReceipt: 'Print Receipt',
  newSale: 'New Sale',

  // Session gate
  sessionRequired: 'Session Required',
  sessionRequiredMsg: 'This shop requires an open cashier session before processing sales.',
  openingCash: 'Opening Cash',
  openingCashPlaceholder: 'Enter cash in drawer',

  // Payment modal
  payment: 'Payment',
  cash: 'Cash',
  card: 'Card',
  bankTransfer: 'Bank Transfer',
  voucher: 'Voucher',
  loyaltyPoints: 'Loyalty Points',
  splitPayment: 'Split Payment',
  amountDue: 'Amount Due',
  amountTendered: 'Amount Tendered',
  cashReceived: 'Cash Received',
  change: 'Change',
  remaining: 'Remaining',
  paymentReference: 'Reference (optional)',
  paymentComplete: 'Sale Complete',
  creditSale: 'Credit Sale',

  // Order discount modal
  applyOrderDiscount: 'Apply Order Discount',
  discountAmount: 'Discount Amount',
  discountPercent: 'Discount %',
  applyFlat: 'Flat Amount',
  applyPercent: 'Percentage',

  // Loyalty modal
  loyaltyBalance: 'Points Balance',
  pointsToRedeem: 'Points to Redeem',
  pointValue: 'Point Value',
  discountPreview: 'Discount',
  minPoints: 'Minimum',
  maxDiscount: 'Max discount',
  redeemPoints: 'Redeem Points',
  removeRedemption: 'Remove Redemption',

  // Hold orders drawer
  holdOrders: 'On Hold Orders',
  noHoldOrders: 'No orders on hold',
  recallOrder: 'Recall',
  holdOrderNote: 'Current cart will be placed on hold',

  // Customer selector
  customerCity: 'City',
  searchCity: 'Search city...',
  loyaltyPointsLabel: 'Loyalty Points',

  // Receipt
  receipt: 'Receipt',
  receiptTitle: 'Sales Receipt',
  orderNumber: 'Order #',
  date: 'Date',
  cashier: 'Cashier',
  items: 'Items',
  payments: 'Payments',
  totalPaid: 'Total Paid',
  balanceDue: 'Balance Due',

  // Order list page
  allPosOrders: 'All Offline Sales Orders',
  viewAndManageAllOrders: 'View and manage all offline sales orders',
  notesPlaceholder: 'Add any notes about this order...',
  filterByStatus: 'Filter by Status',
  filterByPayment: 'Filter by Payment',
  filterByShop: 'Filter by Shop',
  filterByCashier: 'Filter by Cashier',
  dateFrom: 'From',
  dateTo: 'To',
  dateRange: 'Date Range',
  searchOrders: 'Search by order number...',
  searchOrdersPlaceholder: 'Search by order #, customer name, phone...',
  filterByCustomer: 'Filter by Customer',
  filterBySession: 'Filter by Session',
  allCashiers: 'All Cashiers',
  selectSession: 'Select session',
  selectDateRangeFirst: 'Select a date range first',
  noSessionsFound: 'No sessions found',
  searchSessions: 'Search sessions...',

  // Dynamic order status labels — used via t(`offlineSales.status_${order.status}`)
  status_completed:          'Completed',
  status_voided:             'Voided',
  status_on_hold:            'On Hold',
  status_credit:             'Credit',
  status_returned:           'Returned',
  status_partially_returned: 'Partially Returned',

  // Dynamic order status labels keyed by the real OrderStatus.key (offline_* prefix) — used via
  // t(`offlineSales.status_${order.OrderStatus?.key}`). The unprefixed set above stays for
  // delivery-note/credit-note sub-badges, which use their own (unprefixed) status vocabulary.
  status_offline_completed:          'Completed',
  status_offline_credit:             'Credit',
  status_offline_on_hold:            'On Hold',
  status_offline_voided:             'Voided',
  status_offline_returned:           'Returned',
  status_offline_partially_returned: 'Partially Returned',
  status_offline_draft:              'Draft',

  // General/online OrderStatus keys — used on courier-fulfilled offline orders, which reuse the
  // general status set instead of offline_* keys (status_returned already exists above).
  status_pending:       'Pending',
  status_confirmed:     'Confirmed',
  status_hold:          'Hold',
  status_not_confirmed: 'Not Confirmed',
  status_cancelled:     'Cancelled',
  status_in_packing:    'In Packing',
  status_in_courier:    'In Courier',
  status_settled:       'Settled',

  // CreditNote.status / DeliveryNote.status — used via t(`offlineSales.status_${status}`) on the
  // order detail page's Credit Notes / Delivery Notes sections (matches creditNote.js/deliveryNote.js).
  status_applied:   'Applied',
  status_delivered: 'Delivered',

  // Dynamic payment status labels — used via t(`offlineSales.payment_${order.paymentStatus}`)
  payment_paid:    'Paid',
  payment_partial: 'Partial',
  payment_unpaid:  'Unpaid',

  // ReturnNote.refundMethod — used via t(`offlineSales.refundMethod_${refundMethod}`) (matches returnNote.js)
  refundMethod_cash:           'Cash',
  refundMethod_credit_note:    'Credit Note',
  refundMethod_loyalty_points: 'Loyalty Points',

  // Payment method keys — used via t(`offlineSales.${paymentMethodKey}`) in receipts
  bank_transfer: 'Bank Transfer',
  cash: 'Cash',
  card: 'Card',
  payzy: 'Payzy',
  orderNumberCol: 'Order #',
  customerCol: 'Customer',
  cashierCol: 'Cashier',
  totalCol: 'Total',
  paidCol: 'Paid',
  balanceCol: 'Balance',
  statusCol: 'Status',
  paymentStatusCol: 'Payment',
  actionsCol: 'Actions',
  viewOrder: 'View',
  voidOrder: 'Void',
  processReturn: 'Return',
  createDeliveryNote: 'Create Delivery Note',
  recordPayment: 'Record Payment',
  printReceiptAction: 'Print Receipt',

  // Status labels
  statusCompleted: 'Completed',
  statusVoided: 'Voided',
  statusOnHold: 'On Hold',
  statusCredit: 'Credit',
  statusReturned: 'Returned',
  statusPartiallyReturned: 'Partially Returned',
  paymentPaid: 'Paid',
  paymentPartial: 'Partial',
  paymentUnpaid: 'Unpaid',

  // Void
  voidOrderTitle: 'Void Order',
  voidReason: 'Reason for voiding',
  voidReasonPlaceholder: 'Enter reason...',
  confirmVoid: 'Confirm Void',
  voidWarning: 'This action cannot be undone. Stock will be restored.',

  // Return modal
  processReturnTitle: 'Process Return',
  returnItems: 'Items to Return',
  returnQty: 'Return Qty',
  available: 'Available',
  refundAmount: 'Refund Amount',
  refundMethod: 'Refund Method',
  refundCash: 'Cash Refund',
  refundCreditNote: 'Credit Note',
  refundLoyaltyPoints: 'Loyalty Points',
  returnReason: 'Return Reason',
  confirmReturn: 'Confirm Return',
  totalRefund: 'Total Refund',

  // Credit payment
  recordCreditPayment: 'Record Payment',
  outstandingBalance: 'Outstanding Balance',
  amountToPayNow: 'Amount to Pay Now',
  confirmPayment: 'Confirm Payment',

  // Customers page
  allPosCustomers: 'Offline Sales Customers',
  addCustomer: 'Add Customer',
  editCustomer: 'Edit Customer',
  importCustomers: 'Import',
  backToCustomers: 'Back to Customers',
  customerNotes: 'Notes',
  customerSecondaryPhone: 'Secondary Phone',
  totalSpend: 'Total Spend',
  viewHistory: 'View History',
  purchaseHistory: 'Purchase History',
  noPurchaseHistory: 'No purchase history',
  activeCustomer: 'Active',
  inactiveCustomer: 'Inactive',

  // Import page
  importStep1Title: 'Download Template',
  importStep1Desc: 'Download the Excel template, fill in your customer data, then upload it below.',
  importStep2Title: 'Upload File',
  importStep3Title: 'Preview & Import',
  downloadTemplate: 'Download Template',
  dropZoneText: 'Drag & drop your file here, or click to browse',
  dropZoneSub: 'Supports .xlsx, .xls, .csv — max 1,000 rows',
  rowsFound: 'rows found',
  previewShowing: 'Showing first {{shown}} of {{total}} rows',
  andMoreRows: '...and {{count}} more rows',
  importing: 'Importing...',
  importNCustomers: 'Import {{count}} Customers',
  importComplete: 'Import Complete',
  importedCustomers: 'Imported',
  updatedCustomers: 'Updated',
  exportCustomers: 'Export',
  exporting: 'Exporting...',
  skippedRows: 'Skipped',
  importErrors: 'Errors',
  rowLabel: 'Row',
  importAnother: 'Import Another File',

  // Sessions page
  cashierSessions: 'Cashier Sessions',
  openNewSession: 'Open New Session',
  activeSession: 'Active Session',
  noActiveSession: 'No active session',
  closedAt: 'Closed At',
  openedAt: 'Opened At',
  closingCash: 'Closing Cash',
  sessionNotes: 'Notes',
  viewZReport: 'View Z-Report',
  zReport: 'Z-Report',
  zReportTitle: 'End of Session Report',
  totalSales: 'Total Sales',
  storePickupPayments: 'Store Pickup Payments',
  categoryWiseSales: 'Category Wise Sales',
  categoryOther: 'Other',
  salesCount: 'Sales Count',
  paymentBreakdown: 'Payment Breakdown',
  voidSummary: 'Voids',
  returnSummary: 'Returns',
  creditSummary: 'Credit Outstanding',
  cashExpected: 'Cash Expected',
  cashVariance: 'Variance',
  selectCashier: 'Select Cashier',
  createSession: 'Create Session',
  sessionSummary: 'Session Summary',
  closingAmounts: 'Closing Amounts (Enter counted cash per method)',
  totalVariance: 'Total Variance',
  statusOpen: 'Open',
  statusClosed: 'Closed',

  // POS Settings page
  posSettingsDesc: 'Configure loyalty programme and receipt layout for your offline sales shops.',
  loyaltySettings: 'Loyalty Programme',
  enableLoyalty: 'Enable Loyalty Points',
  enableLoyaltyHint: 'Customers earn points on purchases and can redeem them as discounts.',
  pointsPerRupee: 'Points Earned per Rupee',
  pointsPerRupeeHint: 'How many points earned per Rs. 1 spent',
  pointValueRupees: 'Value per Point (Rs.)',
  pointValueHint: 'Rupee value of 1 loyalty point when redeemed',
  minRedeemPoints: 'Minimum Points to Redeem',
  minRedeemHint: 'Minimum points required before a customer can redeem',
  maxRedeemPct: 'Max Redemption (% of order)',
  maxRedeemHint: 'Maximum % of order grand total that can be discounted via points',
  receiptSettings: 'Receipt Settings',
  receiptHeader: 'Receipt Header',
  receiptFooter: 'Receipt Footer',
  receiptHeaderPlaceholder: 'Enter header text (e.g. shop address, thank you message)',
  receiptFooterPlaceholder: 'Enter footer text (e.g. return policy)',
  saveSettings: 'Save Settings',

  // Audit / detail page
  auditHistory: 'Audit History',

  // Errors / validation
  cartEmpty: 'Cart is empty',
  sessionRequiredError: 'Please open a cashier session first',
  insufficientLoyaltyPoints: 'Insufficient loyalty points',
  creditNotAllowed: 'Credit sales are not allowed for this shop or customer',

  // Payment fees display
  paymentFee: 'Fee',
  feeAbsorbed: 'absorbed',
  feeChargedToCustomer: 'to customer',
  totalPaymentFees: 'Total Payment Fees',
  paymentFeesCustomer: 'Fees (Customer)',

  // Receipt share buttons
  shareInvoice: 'Share Invoice',
  sendViaWhatsApp: 'WhatsApp',
  sendViaSms: 'Send SMS',
  sendingInvoice: 'Sending…',
  smsSent: 'SMS Sent',

  // Cost & profit columns (for manage_pos_settings users)
  productCostCol: 'Cost',
  paymentFeesCol: 'Fees',
  profitCol: 'Profit',
  totalProductCost: 'Product Cost',
  grossProfit: 'Gross Profit',
};

// New keys used by the actual CreateOfflineSalesOrderPage / ReceivablesPage components.
const newPageKeys = {
  // Module nav / general
  offlineSalesOrders: 'Offline Sales Orders',
  receivables: 'Receivables',

  // Create order page
  createOfflineSalesOrder: 'Create Offline Sales Order',
  editOfflineSalesOrder: 'Edit Offline Sales Order',
  addNewOfflineOrder: 'Record a counter, credit, or courier-fulfilled offline sale',
  backToOrders: 'Back to Orders',
  shopSelection: 'Shop Selection',
  customerInformation: 'Customer',
  orderItems: 'Order Items',
  fulfillmentMethod: 'Fulfillment Method',
  counterPickup: 'Counter Pickup',
  selfDelivery: 'Self Delivery',
  courier: 'Courier',
  deliveryDetails: 'Delivery Details',
  returns: 'Returns',
  deliveryNotes: 'Delivery Notes',
  creditNotes: 'Credit Notes',
  deliveryNoteNumber: 'Delivery Note #',
  creditNoteNumber: 'Credit Note #',
  appliedAmount: 'Applied',
  deliveredAt: 'Delivered',
  deliveryCity: 'Delivery City',
  deliveryAddress: 'Delivery Address',
  deliveryAddressPlaceholder: 'Enter the delivery address',
  deliveryCharge: 'Delivery Charge',
  packageWeightKg: 'Package Weight (kg)',
  notSet: 'Not set',
  amountPaid: 'Amount Paid',
  paymentDetails: 'Payment Details',
  addPayment: 'Add Payment',
  paymentMethod: 'Payment Method',
  selectPaymentMethod: 'Select payment method',
  amount: 'Amount',
  reference: 'Reference',
  orderDiscount: 'Order Discount',
  orderSummary: 'Order Summary',
  additionalInformation: 'Additional Information',
  shop: 'Shop',
  subtotal: 'Subtotal',
  discount: 'Discount',
  tax: 'Tax',
  grandTotal: 'Grand Total',
  paidAmount: 'Paid Amount',
  balanceAmount: 'Balance (Credit)',
  notes: 'Notes',
  createOrder: 'Create Order',
  someFieldsRequired: 'Please fix the highlighted fields before submitting',
  orderCreatedSuccess: 'Order {orderNumber} created successfully',
  orderUpdatedSuccess: 'Order updated successfully',
  failedToCreateOrder: 'Failed to create order',

  // Credit limit warning
  creditLimitWarningTitle: 'Credit limit exceeded',
  creditLimitWarningMessage: 'This customer’s outstanding balance (including this order) exceeds their credit limit. The order was still created — review before extending further credit.',

  // Items
  searchProducts: 'Search products or scan a barcode',
  noProductsFound: 'No products found',
  removeItem: 'Remove item',
  quantity: 'Qty',
  unitPrice: 'Unit Price',
  lineDiscount: 'Discount',
  lineTotal: 'Total',
  noItemsAdded: 'No items added yet',

  // Customer selector
  selectCustomer: 'Select Customer',
  searchCustomers: 'Search customers by name or phone',
  cashSales: 'Cash Sales',
  noCustomersFound: 'No customers found',
  newCustomer: 'New Customer',
  customerName: 'Customer Name',
  customerNamePlaceholder: 'Enter customer name',
  customerPhone: 'Phone',
  customerEmail: 'Email',
  customerEmailPlaceholder: 'Enter email address',
  customerAddress: 'Address',
  customerAddressPlaceholder: 'Enter address',
  customerNotesPlaceholder: 'Add any notes about this customer',
  creditLimit: 'Credit Limit',
  creditLimitPlaceholder: 'Enter credit limit',
  creditTermDays: 'Credit Term (days)',
  creditTermDaysPlaceholder: 'Enter credit term days',
  failedToCreateCustomer: 'Failed to create customer',
  customerCreatedSuccess: 'Customer created successfully',
  changeCustomer: 'Change',
  salesRep: 'Sales Representative',
  selectSalesRep: 'Select a sales representative',
  assignSalesRep: 'Assign Sales Representative',
  salesRepAssigned: 'Sales representative updated',
  salesRepAssignFailed: 'Failed to update sales representative',
  salesRepSettings: 'Sales Representatives',
  salesRepRoles: 'Sales Rep Roles',
  salesRepRolesPlaceholder: 'All users',
  salesRepRolesHint: 'Only users in these roles can be selected as sales reps on offline orders. If none are selected, all users are listed.',

  // Customer location picker
  customerLocation: 'Location',
  locationSearchPlaceholder: 'Search for a place in Sri Lanka',
  locationSearchButton: 'Search',
  locationSearchNoResults: 'No results found',
  locationUseMyLocation: 'Use My Current Location',
  locationDetecting: 'Detecting your location...',
  locationPermissionDenied: 'Location permission was denied. Please allow location access or select on the map.',
  locationUnavailable: 'Your location could not be determined. Please select on the map.',
  locationTimeout: 'Location request timed out. Please try again or select on the map.',
  locationNotSupported: 'Your browser does not support location detection. Please select on the map.',
  locationNearbyAddress: 'Nearby address',
  locationNearbyAddressUnavailable: 'Could not look up a nearby address for this point',
  locationGoogleLinkLabel: 'Or paste a Google Maps link',
  locationGoogleLinkPlaceholder: 'Paste a Google Maps link',
  locationUseLinkButton: 'Use Link',
  locationGoogleLinkInvalid: 'Could not read coordinates from this link',
  locationLatitude: 'Latitude',
  locationLongitude: 'Longitude',
  locationClear: 'Clear location',

  // Receivables page
  receivablesDescription: 'Outstanding customer balances from credit and partially-returned offline sales',
  totalOutstanding: 'Total Outstanding',
  customer: 'Customer',
  phone: 'Phone',
  aging0to30: '0-30 days',
  aging31to60: '31-60 days',
  aging61to90: '61-90 days',
  aging90plus: '90+ days',
  noReceivablesFound: 'No outstanding receivables',
  noReceivablesFoundDescription: 'All offline sales customer balances are settled',
  failedToLoadReceivables: 'Failed to load receivables',
  searchByCustomerName: 'Search by customer name or phone',
  warehouse: 'Warehouse',
  selectWarehouse: 'Select Warehouse',
  sessionRequired: 'A cashier session is required to process orders in this shop',
  sessionRequiredTitle: 'Cashier Session Required',
  sessionLoading: 'Checking cashier session...',
  sessionNotFound: 'Open a cashier session for this shop before creating an order',
  customerStatement: 'Customer Statement',
  openingBalance: 'Opening Balance',
  closingBalance: 'Closing Balance',
  type: 'Type',
  transactionType_invoice: 'Invoice',
  transactionType_payment: 'Payment Received',
  transactionType_credit_note: 'Credit Note',
  transactionType_return: 'Refund (Cash)',
  debit: 'Debit',
  credit: 'Credit',
  selectDateRange: 'Select date range',
  noTransactionsFound: 'No transactions found',
  noTransactionsFoundDescription: 'No transactions in the selected date range',
  paymentRecordedSuccess: 'Payment recorded successfully',
  failedToRecordPayment: 'Failed to record payment',
  creditNotePaymentMethod: 'Credit Note',
  selectCreditNote: 'Select Credit Note',
  searchCreditNote: 'Search credit notes...',
  noAvailableCreditNotes: 'No available credit notes',
  applyCreditNotePayment: 'Apply Credit Note',

  // Courier packing page
  offlinePacking: 'Offline Courier Packing',
  offlinePackingDescription: 'Prepare courier-fulfilled offline orders for dispatch',
  viewMixedPackingQueue: 'View Mixed Queue',
  offlinePackingBatchList: 'Offline Courier Packing Batches',
  offlinePackingBatchListDescription: 'View and manage all offline courier packing batches',
  courierAccount: 'Courier Account',
  selectCourierAccount: 'Select courier account',
  searchPackingOrders: 'Search order number or customer',
  failedToLoadPackingReferences: 'Failed to load packing references',
  selectPackingReferences: 'Select a warehouse and courier account first',
  failedToLoadPackingOrders: 'Failed to load offline courier orders',
  selectPackingOrders: 'Select at least one order',
  packingBatchCreated: 'Packing batch created successfully',
  failedToCreatePackingBatch: 'Failed to create packing batch',
  selectPackingOrder: 'Select order {orderNumber}',
  selectedPackingOrders: '{count} orders selected',
  createPackingBatch: 'Create Packing Batch',
  noPackingOrders: 'No offline courier orders found',
  noPackingOrdersDescription: 'Select a warehouse and courier account to search eligible orders',
  // Order audit history
  auditEmpty: "Empty",
  auditBy: "by {{name}}",
  audit_CREATE: "Order created",
  audit_UPDATE: "Order updated",
  audit_STATUS_CHANGE: "Status changed",
  audit_DELETE: "Removed",
  audit_payment_CREATE: "Payment recorded",
  audit_payment_UPDATE: "Payment updated",
  audit_payment_DELETE: "Payment removed",
  audit_payment_customer_payment_applied: "Customer payment applied",
  audit_payment_customer_payment_voided: "Customer payment voided",
  audit_payment_cheque_bounced: "Cheque bounced",
  audit_quotation_CONVERTED: "Converted from quotation",
  audit_order_items_UPDATED: "Order items updated",
  audit_order_items_CREATED: "Order items added",
  audit_delivery_note_CREATED: "Delivery note created",
  audit_return_note_CREATED: "Return note created",
  audit_return_note_VOIDED: "Return note voided",
  audit_credit_note_ISSUED: "Credit note issued",
  audit_credit_note_APPLIED: "Credit note applied",
  audit_credit_note_CANCELLED: "Credit note cancelled",
  auditTag_payment: "Payment Record",
  auditTag_quotation: "Quotation",
  auditTag_order_items: "Order Items",
  auditTag_delivery_note: "Delivery Note",
  auditTag_return_note: "Return Note",
  auditTag_credit_note: "Credit Note",
  auditItemAdded: "Added",
  auditItemRemoved: "Removed",
  auditItemUpdated: "Updated",
  auditField_orderStatus: "Order Status",
  auditField_status: "Status",
  auditField_quantity: "Quantity",
  freeUnits: "Free",
  freeUnitsHint: "Units given free of charge (included in quantity)",
  showFreeUnits: "Add free units",
  freeUnitsCustom: "Custom qty",
  freeUnitsApply: "Apply",
  itemsIncludedFree: "{{count}} free item(s) included",
  totalDiscountLabel: "Total Rs. {{total}}",
  inclFreeValue: "incl. Rs. {{value}} for {{count}} free",
  auditField_freeQuantity: "Free Units",
  customerType: "Customer Type",
  selectCustomerType: "Select customer type",
  customerCategory: "Customer Category",
  selectCustomerCategory: "Select customer category",
  addCustomerCategory: "Add new category",
  customerCategoryNamePlaceholder: "Enter category name",
  customerCategoryCreated: "Customer category \"{name}\" created",
  customerCategoryCreateFailed: "Failed to create customer category",
  tinNumber: "TIN Number",
  tinNumberPlaceholder: "Enter TIN number",
  vatNumber: "VAT Number",
  vatNumberPlaceholder: "Enter VAT number",
  auditField_unitPrice: "Unit Price",
  auditField_lineDiscount: "Line Discount",
  auditField_lineTotal: "Line Total",
  auditField_refundMethod: "Refund Method",
  auditField_totalRefundAmount: "Refund Amount",
  auditField_creditNoteNumber: "Credit Note",
  auditField_amount: "Amount",
  auditField_appliedAmount: "Applied Amount",
  auditField_sourceType: "Source",
  auditField_receivedByName: "Received By",
  auditField_notes: "Notes",
  // Customer payments, opening balances & cheques
  statusPending: 'Pending',
  statusInCourier: 'In Courier',
  transactionType_opening_balance: 'Opening Balance',
  transactionType_customer_payment: 'Customer Payment',
  transactionType_customer_payment_reversal: 'Payment Reversed',
  chqDetails: 'Cheque Details',
  chqDate: 'Cheque Date',
  chqPostDated: 'Post-dated cheque',
  chqPostDatedHint: 'Tick if the cheque is dated for a future day — a cheque date is then required',
  chqSelectDate: 'Select date',
  chqDateRequired: 'Cheque date is required for a post-dated cheque',
  chqDateMustBeFuture: 'Post-dated cheque date must be after today',
  chqTitle: 'Cheques',
  chqDescription: 'Track received cheques, see which can be deposited today and record deposit, clearance or bounce',
  chqView_all: 'All',
  chqView_depositable: 'Depositable Now',
  chqView_post_dated: 'Post-dated (Not Due)',
  chqView_deposited: 'Deposited',
  chqView_cleared: 'Cleared',
  chqView_bounced: 'Bounced',
  chqStatus_pending: 'Pending',
  chqStatus_deposited: 'Deposited',
  chqStatus_cleared: 'Cleared',
  chqStatus_bounced: 'Bounced',
  chqView_cancelled: 'Cancelled',
  chqStatus_cancelled: 'Cancelled',
  chqMarkDeposited: 'Mark as deposited',
  chqMarkCleared: 'Mark as cleared',
  chqMarkBounced: 'Mark as bounced',
  chqBounceConfirm: 'Mark this cheque as bounced? The payment it covered will be reversed and the invoices will be outstanding again.',
  chqDepositConfirm: 'Mark this cheque as deposited?',
  chqStatusUpdated: 'Cheque status updated',
  chqFailedToUpdate: 'Failed to update cheque',
  chqFailedToLoad: 'Failed to load cheques',
  chqNotYetDue: 'Not yet due for deposit',
  chqDueInDays: 'Due in {days} day(s)',
  chqNotYetDueOn: 'Not due for deposit until {date} ({days} day(s) left)',
  cpPaymentDate: 'Payment Date',
  cpReference: 'Reference',
  cpProof: 'Payment Proof',
  cpViewProof: 'View proof',
  cpViewFile: 'View file',
  cpView: 'View payment',
  cpFilterCustomer: 'Customer',
  cpFilterMethod: 'Payment Method',
  cpDetailTitle: 'Customer Payment',
  cpAllocations: 'Applied To',
  cpAllocatedTo: 'Invoice / Balance',
  cpAsOfDate: 'As of Date',
  cpInvoiceTotal: 'Invoice Total',
  cpAmountApplied: 'Amount Applied',
  cpReceivedBy: 'Received By',
  cpNoAllocations: 'This payment has not been applied to any balance',
  chqPdcTag: 'Post-dated',
  chqLinkedTo: 'Linked To',
  chqCount: '{count} cheque(s)',
  chqSearchPlaceholder: 'Search customer',
  chqDateRange: 'Cheque date range',
  chqEmpty: 'No cheques found',
  chqEmptyDescription: 'No cheques match the selected view',
  chqPdcInHand: 'Cheques In Hand',
  cpTitle: 'Customer Payments',
  cpDescription: 'Payments received from customers, applied automatically to the oldest outstanding balances first',
  cpReceivePayment: 'Receive Payment',
  cpReceiveDescription: 'Record a customer payment without selecting an invoice — it is applied to the opening balance first, then invoices oldest-first',
  cpPaymentDetails: 'Payment Details',
  cpMethod: 'Payment Method',
  cpNotes: 'Notes',
  cpSave: 'Save Payment',
  cpSaved: 'Customer payment recorded',
  cpFailedToSave: 'Failed to record customer payment',
  cpFailedToLoad: 'Failed to load customer payments',
  cpFailedToPreview: 'Failed to load outstanding items',
  cpSelectCustomerFirst: 'Select a customer to see their outstanding balances',
  cpSelectMethod: 'Select a payment method',
  cpAmountRequired: 'Amount must be greater than 0',
  cpAllocationPreview: 'Allocation Preview',
  cpFifoHint: 'Applied oldest first: opening balance, then invoices by date',
  cpOutstanding: 'Outstanding',
  cpApplied: 'Applied',
  cpRemaining: 'Remaining',
  cpNoOpenItems: 'Nothing outstanding',
  cpNoOpenItemsDescription: 'This customer has no outstanding balance — the payment will be kept as credit',
  cpExistingCredit: 'Existing unallocated credit',
  cpWillBeCredit: '{amount} will be kept as customer credit',
  cpNumber: 'Receipt No.',
  cpUnallocated: 'Unallocated',
  cpUnallocatedCredit: 'Unallocated Credit',
  cpAppliedTo: 'Applied to',
  cpStatus_active: 'Active',
  cpStatus_void: 'Voided',
  cpVoid: 'Void payment',
  cpVoidConfirm: 'Void payment {number}? Its allocations will be reversed and the balances will be outstanding again.',
  cpVoided: 'Customer payment voided',
  cpFailedToVoid: 'Failed to void customer payment',
  cpSearchPlaceholder: 'Search receipt no., reference or customer',
  cpEmpty: 'No customer payments yet',
  cpEmptyDescription: 'Payments received with "Receive Payment" will appear here',
  obAmount: 'Opening Balance Amount',
  obAsOfDate: 'As of Date',
  obDateRequired: 'As of date is required',
  obAmountRequired: 'Opening balance is required when a date is set',
  cusCode: 'Customer Code',
  cusCodePlaceholder: 'Auto-generated if empty',
  cusCodeExists: 'This customer code is already in use',
  cusCodeInvalid: 'Use only letters, numbers and dashes (max 20)',
  nameRequired: 'Customer name is required',
  invalidPhone: 'Enter a valid phone number',
  phoneExists: 'A customer with this phone number already exists',
  secondarySameAsPrimary: 'Secondary phone must differ from the primary phone',
  invalidEmail: 'Enter a valid email address',
  mustBeNonNegative: 'Must be 0 or more',
  mustBeWholeNumber: 'Must be a whole number, 0 or more',
  obDateFuture: 'As of date cannot be in the future',
  obLocked: '{paid} has already been paid against this balance — void those payments to edit it',
};

export const offlineSales = {
  ...posLegacyKeys,
  ...newPageKeys,
};
